import { useEffect, useState } from 'react';

import toursApi from '../api/tours/api';

/**
 * The name of the tour a travel file belongs to, for `<Tour Name>` in an email.
 *
 * The travel file stores only the tour's id (`tourId`, or `relatedServiceId` on a
 * registration file), and buildMergeContext deliberately takes the name from its
 * caller. Fetched through the API directly rather than ToursContext.getOne, which
 * would also replace the context's selected tour under whatever else is using it.
 * '' until it resolves, or when the file has no tour.
 */
export type TravelFileTour = {
    _id: string | number;
    name: string;
    code1?: string | null;
    code2?: string | null;
    startDate?: string | null;
    endDate?: string | null;
};

/**
 * The tour record behind a travel file — name and dates. A GROUP file books no
 * tour service of its own (the tour services sit on its client files), so its
 * tour is only reachable this way, through `tourId`. Null until it resolves.
 */
export const useTravelFileTour = (travelFile: any): TravelFileTour | null => {
    // The file's own tourId, else relatedServiceId (registration files), else
    // its tour service's tourId — imported client files often have only that.
    const tourServiceTourId = (travelFile?.services || []).find((s: any) => s?.type === 'tours' && s?.tourId)?.tourId;
    const tourId = travelFile?.tourId?._id ?? travelFile?.tourId ?? travelFile?.relatedServiceId ?? tourServiceTourId;
    const [tour, setTour] = useState<TravelFileTour | null>(null);

    useEffect(() => {
        setTour(null);
        if (!tourId) return undefined;

        let cancelled = false;
        Promise.resolve(toursApi.getOne({ _id: tourId } as any))
            .then((row: any) => {
                if (!cancelled && row)
                    setTour({
                        _id: row._id ?? tourId,
                        name: row.name || '',
                        code1: row.code1,
                        code2: row.code2,
                        startDate: row.startDate,
                        endDate: row.endDate
                    });
            })
            .catch(() => {
                // A missing tour only blanks <Tour Name>; the rest of the message resolves.
            });

        return () => {
            cancelled = true;
        };
    }, [tourId]);

    return tour;
};

export const useTravelFileTourName = (travelFile: any): string => useTravelFileTour(travelFile)?.name || '';

export default useTravelFileTourName;
